import React, { useContext } from 'react';
import {
  Theme,
  makeStyles,
  InputBase,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import {
  NonHoverBorder,
  BlackHeadings,
  BlackSmall,
  GraySmall,
} from '../../theme/colors';
import { CloseIcon, SearchIcon } from '../Icons';
import * as Colors from '../../theme/colors';
import { RouteContext } from '../../context';
import { updateSearchValueAction } from '../../store/ui/actions';
import { typography13MediumStyle, typography13RegularStyle } from '../Text';
import { RootState } from '../../store';
import { PrimaryButtonShadow } from '../../theme/shadows';

type StyleProps = {
  showIconOnly: boolean;
  fullWidthOnFocus?: boolean;
};

const INITIAL_SEARCH_WIDTH = 50;
const SEARCH_ICON_BUTTON_WIDTH = 0;
const FULL_SEARCH_WIDTH = 298;
const FULL_SEARCH_WIDTH_XS = 328;
const COLLAPSED_SEARCH_WIDTH = 130;
const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    '& .MuiInputBase-input': {
      paddingLeft: theme.spacing(2),
      fontSize: typography13MediumStyle.fontSize,
      fontWeight: typography13MediumStyle.fontWeight,
      transition: 'width 0.5s',
      width: (props: StyleProps) =>
        props.showIconOnly ? SEARCH_ICON_BUTTON_WIDTH : INITIAL_SEARCH_WIDTH,
      '&::placeholder': {
        opacity: 1,
      },
      '&:not(:placeholder-shown)': {
        width: FULL_SEARCH_WIDTH,
        [theme.breakpoints.down('sm')]: {
          width: props =>
            props.fullWidthOnFocus ? FULL_SEARCH_WIDTH : COLLAPSED_SEARCH_WIDTH,
        },
        [theme.breakpoints.down('xs')]: {
          width: props =>
            props.fullWidthOnFocus
              ? FULL_SEARCH_WIDTH_XS
              : COLLAPSED_SEARCH_WIDTH,
        },
      },
      '&:hover': {
        cursor: 'pointer',
      },
    },
    '&.MuiInputBase-root': {
      border: `1px solid ${NonHoverBorder}`,
      boxShadow: (props: StyleProps) =>
        props.showIconOnly ? PrimaryButtonShadow : 'unset',
      height: 28,
      color: BlackSmall,
      borderRadius: theme.spacing(0.5),
      '&.Mui-focused': {
        '&.MuiInputBase-root': {
          border: `1px solid ${BlackSmall}`,
        },
        '& .MuiInputBase-input': {
          width: FULL_SEARCH_WIDTH,
          [theme.breakpoints.down('sm')]: {
            width: props =>
              props.fullWidthOnFocus
                ? FULL_SEARCH_WIDTH
                : COLLAPSED_SEARCH_WIDTH,
          },
          [theme.breakpoints.down('xs')]: {
            width: props =>
              props.fullWidthOnFocus
                ? FULL_SEARCH_WIDTH_XS
                : COLLAPSED_SEARCH_WIDTH,
          },
          '&::placeholder': {
            color: BlackHeadings,
            fontSize: typography13RegularStyle.fontSize,
            fontWeight: typography13RegularStyle.fontWeight,
          },
        },
      },
      '&:hover': {
        backgroundColor: Colors.HoverBackground,
        color: BlackHeadings,
        cursor: 'pointer',
      },
    },
  },
  iconContainer: {
    padding: theme.spacing(0, 0.5, 0, 1),
    display: 'flex',
    alignItems: 'center',
    height: 0,
    width: 0,
    '&:hover': {
      cursor: 'pointer',
    },
  },
  searchIcon: {
    height: 12,
    width: 12,
    marginRight: theme.spacing(1),
    color: Colors.BlackSmall,
  },
  closeIcon: {
    width: 8,
    height: 8,
    position: 'absolute',
    right: 0,
    paddingRight: theme.spacing(1.5),
    color: GraySmall,
    '&:hover': {
      color: BlackHeadings,
    },
  },
  iconButton: {
    padding: 'unset',
  },
  separator: {
    margin: theme.spacing(0, 2),
    height: 22,
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(0, 1.5),
    },
  },
}));

type Props = {
  showIconOnly: boolean;
  onFocused?: () => void;
  onBlurred?: () => void;
  fullWidthOnFocus?: boolean;
};

export const Searchbox = ({
  showIconOnly,
  onFocused,
  onBlurred,
  fullWidthOnFocus,
}: Props) => {
  const classes = useStyles({ showIconOnly, fullWidthOnFocus });
  const dispatch = useDispatch();
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const { pathname } = useContext(RouteContext);
  const searchInputValue = useSelector(
    (state: RootState) => state.ui.searchValue,
  );

  const handleEscape = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      if (searchInputRef.current) {
        // un-focus input
        searchInputRef.current.blur();
      }
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event && event.target) {
      return dispatch(updateSearchValueAction(event.target.value));
    }
    return dispatch(updateSearchValueAction(''));
  };

  const resetSearch = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
    dispatch(updateSearchValueAction(''));
  };

  const handleKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    const debouncedSearch = debounce(() => handleSearch(event), 500);
    debouncedSearch();
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  React.useEffect(
    () => () => {
      if (searchInputRef.current) {
        searchInputRef.current.value = '';
      }
    },
    [pathname],
  );

  return (
    <InputBase
      inputRef={searchInputRef}
      className={classes.input}
      startAdornment={
        <div className={classes.iconContainer}>
          <SearchIcon className={classes.searchIcon} />
        </div>
      }
      endAdornment={
        searchInputValue ? (
          <IconButton onClick={resetSearch} className={classes.iconButton}>
            <CloseIcon color="primary" className={classes.closeIcon} />
          </IconButton>
        ) : null
      }
      placeholder="Search"
      onFocus={e => {
        e.target.placeholder = 'Find in view...';
        if (onFocused) {
          onFocused();
        }
      }}
      onBlur={e => {
        e.target.placeholder = 'Search';
        if (onBlurred) {
          onBlurred();
        }
      }}
      onChange={handleKeyChange}
      onKeyDown={handleEscape}
    />
  );
};

type TableSearchProps = {
  showDivider?: boolean;
};

export const TableSearch = ({ showDivider = true }: TableSearchProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useStyles({
    showIconOnly: isSmallScreen,
    fullWidthOnFocus: false,
  });

  return (
    <div className={classes.root}>
      <Searchbox showIconOnly={isSmallScreen} />
      {showDivider && (
        <Divider orientation="vertical" className={classes.separator} />
      )}
    </div>
  );
};
